// ============================================================================
// RoutePanel — the Route Optimizer's bottom panel (2026-07-31 run).
//
// Owns ZERO state: everything lives in useRoutePlanner (the fence — the map
// stays route-agnostic; this panel is a pure consumer). Collapsible so the
// polyline stays visible on a phone while scrolling stops (Gate 4).
//
// Miles are haversine and labeled "approx" everywhere (D-1). Handoff per
// D-3: ≤10 stops one Google Maps link, >10 a "Navigate to next stop" flow.
// Drag-to-reorder is pointer-event based (works with touch, no new deps);
// manual order always wins — reorders never re-run the optimizer.
// ============================================================================

import { useRef, useState } from 'react';
import { ChevronDown, ChevronUp, GripVertical, X } from 'lucide-react';
import type { PlannerStop, RoutePlanner } from './use-route-planner';
import {
  buildDirectionsUrl,
  buildLegUrl,
  handoffModeFor,
  type LatLng,
} from './route-links';
import { buildNavigateUrl } from './navigate-link';
import { useNavProvider } from './navigate-button';
import { MAX_ROUTE_STOPS } from './route-order';

const fmtMiles = (m: number): string => `${m.toFixed(1)} mi`;

/** row height must match the h-11 (44px) rows below — touch target floor */
const ROW_PX = 44;

export function RoutePanel({
  planner,
  addAllCount,
  onAddAll,
}: {
  planner: RoutePlanner;
  /** size of the current filtered set; Add-all only offered when ≤ cap */
  addAllCount: number;
  onAddAll: () => void;
}) {
  const { state } = planner;
  const [collapsed, setCollapsed] = useState(false);
  const [optimizing, setOptimizing] = useState(false);
  const [dragFrom, setDragFrom] = useState<number | null>(null);
  const [dragTo, setDragTo] = useState<number | null>(null);
  const listRef = useRef<HTMLOListElement | null>(null);
  const dragStartY = useRef(0);
  // CW-C Phase 1 — must be called BEFORE the early return below; hooks cannot
  // be conditional.
  const [navProvider] = useNavProvider();

  if (!state.active) return null;

  const planned = state.orderedIds !== null;
  const stopById = new Map(state.stops.map((s) => [s.id, s]));
  const orderedStops = (state.orderedIds ?? [])
    .map((id) => stopById.get(id))
    .filter((s): s is NonNullable<typeof s> => s !== undefined);
  const mode = handoffModeFor(orderedStops.length);
  const next = orderedStops[state.nextIndex];

  const runOptimize = async () => {
    setOptimizing(true);
    try {
      await planner.optimize();
    } finally {
      setOptimizing(false);
    }
  };

  // pointer-event drag: rows are uniform ROW_PX tall, so the target index is
  // start index + rows moved. Works for mouse AND touch (no HTML5 DnD).
  const onDragStart = (index: number) => (e: React.PointerEvent) => {
    e.preventDefault();
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
    dragStartY.current = e.clientY;
    setDragFrom(index);
    setDragTo(index);
  };
  const onDragMove = (e: React.PointerEvent) => {
    if (dragFrom === null) return;
    const moved = Math.round((e.clientY - dragStartY.current) / ROW_PX);
    const to = Math.max(0, Math.min(orderedStops.length - 1, dragFrom + moved));
    setDragTo(to);
  };
  const onDragEnd = () => {
    if (dragFrom !== null && dragTo !== null && dragFrom !== dragTo) {
      planner.reorder(dragFrom, dragTo);
    }
    setDragFrom(null);
    setDragTo(null);
  };

  // MULTI-STOP handoff. Route MATH — stays on coordinates, stays Google (the
  // only provider with a documented multi-waypoint URL we rely on). HARD RULE:
  // do not make this address-based.
  const openMapsUrl = (): string => {
    const pts: LatLng[] = orderedStops.map((s) => ({ lat: s.lat, lng: s.lng }));
    return buildDirectionsUrl(pts, state.start ?? undefined);
  };

  // SINGLE-DESTINATION handoff (>10-stop leg-by-leg mode). Different job,
  // different input: through the user's chosen provider, so the routes surface
  // matches the facility card and the map sheet. CW-C Phase 4 — coordinate-first
  // when the stop's coordinate has ROOFTOP provenance, address otherwise. Falls
  // back to the coordinate-only leg URL if the builder can produce nothing.
  const nextLegUrl = (stop: PlannerStop): string =>
    buildNavigateUrl(navProvider, {
      address: stop.address,
      lat: stop.lat,
      lng: stop.lng,
      coordTrusted: stop.coordTrusted,
      name: stop.name,
    }) ?? buildLegUrl(stop);

  return (
    <div
      role="region"
      aria-label="Route plan"
      className="absolute inset-x-0 bottom-0 z-20 rounded-t-2xl border-t border-tcs-border bg-tcs-overlay shadow-[0_-8px_30px_-12px_rgba(22,24,29,0.3)]"
    >
      {/* header — always visible, 44px+ touch targets */}
      <div className="flex items-center gap-2 px-4 py-2">
        <button
          type="button"
          onClick={() => setCollapsed(!collapsed)}
          aria-expanded={!collapsed}
          aria-label={collapsed ? 'Expand route panel' : 'Collapse route panel'}
          className="flex h-11 min-w-11 items-center justify-center gap-1 rounded-lg text-tcs-text-muted hover:bg-tcs-bg"
        >
          {collapsed ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
        </button>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold">
            Route plan
            <span className="ml-2 rounded-full bg-tcs-bg px-2 py-0.5 font-mono text-[11px]">
              {state.stops.length} stop{state.stops.length === 1 ? '' : 's'}
            </span>
          </div>
          {planned && state.totalMiles !== null && (
            <div className="font-mono text-[11px] text-tcs-text-muted">
              ~{fmtMiles(state.totalMiles)} total (approx, straight-line)
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={planner.exit}
          className="h-11 shrink-0 rounded-lg border border-tcs-border px-3 text-xs font-semibold"
        >
          Exit planning
        </button>
      </div>

      {!collapsed && (
        <div className="max-h-[38vh] overflow-y-auto px-4 pb-4">
          {state.capHint && (
            <div className="mb-2 rounded-lg bg-tcs-bg px-3 py-2 text-xs">
              Route planning caps at {MAX_ROUTE_STOPS} stops — filter the map
              down and plan the rest as a second run.
            </div>
          )}
          {state.planError && (
            <div className="mb-2 rounded-lg bg-tcs-bg px-3 py-2 text-xs">
              {state.planError}
            </div>
          )}

          {!planned && (
            <p className="mb-2 text-xs text-tcs-text-muted">
              Tap pins to add stops (tap again to remove), then Optimize for
              the shortest visit order. Filters and search keep working while
              you select.
            </p>
          )}

          <div className="mb-2 flex flex-wrap gap-2">
            {addAllCount > 0 && addAllCount <= MAX_ROUTE_STOPS && (
              <button
                type="button"
                onClick={onAddAll}
                className="h-11 rounded-lg border border-tcs-border px-3 text-xs font-semibold"
              >
                Add all visible ({addAllCount})
              </button>
            )}
            <button
              type="button"
              onClick={() => void runOptimize()}
              disabled={state.stops.length === 0 || optimizing}
              className="h-11 rounded-lg bg-tcs-inverse px-4 text-xs font-semibold text-tcs-inverse-ink disabled:opacity-40"
            >
              {optimizing ? 'Optimizing…' : planned ? 'Re-optimize' : 'Optimize'}
            </button>
            {state.stops.length === 0 && (
              <span className="self-center text-[11px] text-tcs-text-muted">
                Add at least one stop to optimize
              </span>
            )}
          </div>

          {planned && (
            <ol ref={listRef} className="mb-2" aria-label="Ordered stops">
              {orderedStops.map((s, i) => {
                const leg = state.legs?.[i];
                const isDragging = dragFrom === i;
                // preview shift while a row is being dragged past this one
                let shift = 0;
                if (dragFrom !== null && dragTo !== null && !isDragging) {
                  if (dragFrom < dragTo && i > dragFrom && i <= dragTo) shift = -ROW_PX;
                  if (dragFrom > dragTo && i >= dragTo && i < dragFrom) shift = ROW_PX;
                }
                return (
                  <li
                    key={s.id}
                    className={
                      'flex h-11 items-center gap-2 border-b border-tcs-border/60 bg-tcs-raised text-xs ' +
                      (isDragging ? 'relative z-10 opacity-70' : '')
                    }
                    style={{
                      transform: isDragging
                        ? undefined
                        : shift
                          ? `translateY(${shift}px)`
                          : undefined,
                      transition: dragFrom !== null ? 'transform 120ms' : undefined,
                    }}
                  >
                    <span
                      role="button"
                      tabIndex={0}
                      aria-label={`Reorder ${s.name}`}
                      onPointerDown={onDragStart(i)}
                      onPointerMove={onDragMove}
                      onPointerUp={onDragEnd}
                      onPointerCancel={onDragEnd}
                      className="flex h-11 w-11 shrink-0 cursor-grab touch-none items-center justify-center text-tcs-text-muted"
                    >
                      <GripVertical className="h-4 w-4" />
                    </span>
                    <span className="grid h-5 w-5 shrink-0 place-items-center rounded-full bg-tcs-inverse font-mono text-[10px] font-bold text-tcs-inverse-ink">
                      {i + 1}
                    </span>
                    <span className="min-w-0 flex-1 truncate font-medium">{s.name}</span>
                    <span className="shrink-0 font-mono text-[10px] text-tcs-text-muted">
                      {leg ? `+${fmtMiles(leg.miles)}` : ''}
                    </span>
                    <button
                      type="button"
                      onClick={() => planner.removeStop(s.id)}
                      aria-label={`Remove ${s.name}`}
                      className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg text-tcs-text-muted hover:bg-tcs-bg"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </li>
                );
              })}
            </ol>
          )}

          {planned && orderedStops.length > 0 && mode === 'single-link' && (
            <a
              href={openMapsUrl()}
              target="_blank"
              rel="noreferrer"
              className="flex h-11 items-center justify-center rounded-lg bg-tcs-inverse px-4 text-xs font-semibold text-tcs-inverse-ink"
            >
              Open in Google Maps
            </a>
          )}

          {planned && mode === 'next-stop' && next && (
            <div className="flex items-center gap-2">
              <a
                href={nextLegUrl(next)}
                target="_blank"
                rel="noreferrer"
                onClick={(e) => {
                  // Open the CURRENT leg explicitly, then advance. Relying on
                  // the anchor's default action is an off-by-one: advance()
                  // re-renders the href to the NEXT leg before the browser
                  // resolves the navigation (found in the Gate-3 walk).
                  e.preventDefault();
                  window.open(nextLegUrl(next), '_blank', 'noopener');
                  planner.advance();
                }}
                className="flex h-11 min-w-0 flex-1 items-center justify-center truncate rounded-lg bg-tcs-inverse px-4 text-xs font-semibold text-tcs-inverse-ink"
              >
                Navigate to next stop — {next.name}
              </a>
              <span className="shrink-0 font-mono text-[11px] text-tcs-text-muted">
                Stop {state.nextIndex + 1} of {orderedStops.length}
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
